import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import {
    Download,
    InsertDriveFile,
} from '@mui/icons-material'
import { Box, Typography, useTheme, Link, Input, Button, TextField, capitalize } from '@mui/material'
import { HomeworkAnalysisState, File } from '../../../../features/types'
import { homeworkApiSlice } from '../../../../features/section/homeworkSlice'
import { H5, H6 } from '../../../../layouts/TextStyles'
import { toast } from 'react-toastify'
import { useHeadScope } from '../../../head/headScope'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    homework: HomeworkAnalysisState
    refetch?: () => void
}

const HomeworkCardSection: React.FC<Props> = ({ homework, refetch }) => {
    const { formatMessage } = useIntl()
    const { readOnly } = useHeadScope()
    const theme = useTheme()
    const [pointInput, setPointInput] = useState<number | ''>(homework.point ?? '')
    const [feedbackInput, setFeedbackInput] = useState<string>(homework.feedback ?? '')
    const [gradeHomework, { isLoading: isGrading }] = homeworkApiSlice.useGradeHomeworkMutation()

    const styles = {
        wrapper: {
        },

        filesContainer: {
            p: 2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px',
        },
        sectionTitle: {
            fontSize: '14px',
            fontWeight: 600,
            mb: 1,
            color: theme.palette.text.secondary,
        },
        fileItem: {
            display: 'flex',
            alignItems: 'center',
            p: 1,
            mb: 1,
            backgroundColor: ui.card,
            borderRadius: '4px',
            border: `1px solid ${theme.palette.grey[200]}`,
        },
        fileIcon: {
            mr: 1,
            color: theme.palette.text.secondary,
        },
        fileLink: {
            textDecoration: 'none',
            color: theme.palette.primary.main,
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        downloadIcon: {
            ml: 'auto',
            color: theme.palette.text.secondary,
            cursor: 'pointer',
            '&:hover': {
                color: theme.palette.primary.main,
            },
        },
        pointInput: {
            width: '100px',
            height: '40px',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.grey[200]}`,
            padding: '0 16px',
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
        },
        feedbackInput: {
            width: '100%',
            minHeight: '120px',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.grey[200]}`,

            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
        },
        feedbackContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px'
        },
    }

    const handleDownload = (file: File) => {
        window.open(file.link, '_blank')
    }

    const handlePointChange = (value: string) => {
        if (value === '') {
            setPointInput('')
            return
        }
        const parsed = Math.floor(Number(value))
        if (Number.isNaN(parsed)) {
            return
        }
        const clamped = Math.max(0, Math.min(100, parsed))
        setPointInput(clamped)
    }

    const handleFeedbackChange = (value: string) => {
        setFeedbackInput(value)
    }

    const handleGradeHomework = async (id: number) => {
        try {
            await gradeHomework({
                homeworkId: id,
                body: {
                    point: typeof pointInput === 'number' ? pointInput : 0,
                    feedback: feedbackInput,
                },
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            refetch?.()
        } catch (error) {
            toast.error(formatMessage({ id: 'submit_error' }))
        }
    }

    return (
        <Box sx={styles.wrapper}>
            <Box sx={styles.filesContainer}>

                <H6 color={ui.ink} style={{ marginBottom: '20px' }}>{homework.homework.description ? capitalize(homework.homework.description) : ''}</H6>

                {/* Homework assignment files */}
                {homework.homework.files && homework.homework.files.length > 0 && (
                    <Box>
                        <Typography sx={styles.sectionTitle}>
                            Файлы задания:
                        </Typography>
                        {homework.homework.files.map((file, index) => (
                            <Box key={index} sx={styles.fileItem}>
                                <InsertDriveFile sx={styles.fileIcon} />
                                <Link
                                    href={file.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={styles.fileLink}
                                >
                                    {file.title}
                                </Link>
                                <Download
                                    sx={styles.downloadIcon}
                                    onClick={() => handleDownload(file)}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Student submitted files */}
                {homework.files && homework.files.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={styles.sectionTitle}>
                            Файлы студента:
                        </Typography>
                        {homework.files.map((file, index) => (
                            <Box key={index} sx={styles.fileItem}>
                                <InsertDriveFile sx={styles.fileIcon} />
                                <Link
                                    href={file.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={styles.fileLink}
                                >
                                    {file.title}
                                </Link>
                                <Download
                                    sx={styles.downloadIcon}
                                    onClick={() => handleDownload(file)}
                                />
                            </Box>
                        ))}
                        {homework.description && (
                            <Typography sx={{ ...styles.sectionTitle, marginTop: 2 }}>
                                Ответ студента: {homework.description}
                            </Typography>
                        )}
                    </Box>

                )}

                {/* Feedback if available */}
                {homework.feedback && (
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={styles.sectionTitle}>
                            Оценка: {homework.point} баллов
                        </Typography>
                        <Typography sx={{ fontSize: '16px', color: theme.palette.text.secondary }}>
                            Комментарий к заданию:  {homework.feedback}
                        </Typography>
                    </Box>
                )}
            </Box>
            {homework.status === 'SUBMITTED' && !readOnly && (
                <Box sx={styles.feedbackContainer}>
                    <TextField
                        multiline
                        minRows={4}
                        maxRows={12}
                        sx={styles.feedbackInput}
                        placeholder="Введите обратную связь"
                        value={feedbackInput}
                        onChange={(e) => handleFeedbackChange(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                        <H5>Баллы:</H5>
                        <Input
                            sx={styles.pointInput}
                            placeholder="Введите баллы"
                            value={pointInput}
                            type="number"
                            inputProps={{ min: 0, max: 100, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                            onKeyDown={(e) => {
                                const invalidKeys = ['e', 'E', '+', '-', '.']
                                if (invalidKeys.includes(e.key)) {
                                    e.preventDefault()
                                }
                            }}
                            onChange={(e) => handlePointChange(e.target.value)}
                            onBlur={(e) => handlePointChange(e.target.value)}
                        />
                        <Button
                            sx={{ height: '40px' }}
                            variant="contained"
                            color="primary"
                            disabled={isGrading}
                            onClick={() => handleGradeHomework(homework.id)}
                        >
                            {formatMessage({ id: 'grade_homework' })}
                        </Button>

                    </Box>
                </Box>
            )}
        </Box>
    )
}

export default HomeworkCardSection
